/**
 * Client-side view of the `scope` claim EFG enforces (ENG-3878). A session minted
 * from an emailed preferences link carries `preferences_only` and may reach the
 * preferences endpoints and nothing else.
 *
 * UI only. EFG's DescopeGuard is the authority — this exists so the resident
 * isn't offered actions that would 403.
 */
export const PREFERENCES_ONLY_SCOPE = 'preferences_only';

/**
 * Paths a `preferences_only` session may call, mirroring the gateway's
 * `@AllowScopedToken` routes. Sending the token anywhere else earns a 403,
 * including on routes that would otherwise accept an anonymous request.
 *
 * Kept in step by hand with EFG's `participant/residents/residents.controller.ts`
 * (GET/PATCH `/residents/preferences`, GET/PATCH `/residents/subscriptions`, PATCH
 * `/residents/subscriptions/:subscriptionId`). Nothing enforces that across repos,
 * so the gateway interceptor logs every withheld request — a route added there and
 * missed here shows up as that log line rather than an unexplained 401.
 */
const PREFERENCES_SCOPED_PATHS = ['/residents/subscriptions', '/residents/preferences'];

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * Decodes a JWT's unpadded base64url segment. Hand-rolled rather than `atob`,
 * which jsdom rejects valid input from, leaving the parsing untestable.
 */
/* eslint-disable no-bitwise -- base64 decoding is bit manipulation by definition */
const decodeBase64UrlSegment = (segment: string): string => {
  const normalized = segment.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;

  Array.from(normalized).forEach((char) => {
    const value = BASE64URL_ALPHABET.indexOf(char);
    if (value === -1) throw new Error('Invalid base64url character');
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  });

  return new TextDecoder().decode(new Uint8Array(bytes));
};
/* eslint-enable no-bitwise */

// The JWT's root claims, or undefined for a token that cannot be read.
const readTokenClaims = (jwt?: string): Record<string, unknown> | undefined => {
  const payload = jwt?.split('.')[1];
  if (!payload) return undefined;
  try {
    const claims: unknown = JSON.parse(decodeBase64UrlSegment(payload));
    return typeof claims === 'object' && claims !== null ? { ...claims } : undefined;
  } catch {
    return undefined;
  }
};

const readStringClaim = (jwt: string | undefined, name: string): string | undefined => {
  const value = readTokenClaims(jwt)?.[name];
  return typeof value === 'string' && value ? value : undefined;
};

/**
 * Reads `scope` from the JWT root. Claims under `nsec` are ignored, matching the
 * gateway: they are client-settable, so trusting them would invent a scope that
 * EFG never granted.
 *
 * `scope` rides in the same `generateEmbeddedLink` custom claims as `residentId`,
 * which EFG also reads from the root — so if Descope ever stopped placing them
 * there, every preferences call would 401 on the missing `residentId` rather than
 * quietly handing out an unrestricted session.
 *
 * A token that can't be read is treated as unscoped: the gateway still enforces,
 * and guessing here would lock a legitimate resident out of the UI.
 */
export const readTokenScope = (jwt?: string): string | undefined => readStringClaim(jwt, 'scope');

/** Reads `sub`, the Descope user the session belongs to: who a request made with this token acts as. */
export const readTokenSubject = (jwt?: string): string | undefined => readStringClaim(jwt, 'sub');

export const isPreferencesScopedPath = (url?: string): boolean =>
  Boolean(url) && PREFERENCES_SCOPED_PATHS.some((path) => (url as string).includes(path));

/**
 * Whether this token may be sent to `url`. The single decision point: it reads the
 * scope off the token itself rather than trusting each caller that registers an
 * EFG token provider to remember the rule.
 *
 * Ordinary interactive sessions carry no scope and are unaffected.
 */
export const isTokenAllowedForUrl = (token: string, url?: string): boolean =>
  readTokenScope(token) !== PREFERENCES_ONLY_SCOPE || isPreferencesScopedPath(url);
